import { useState, useRef } from 'react';
import { usePollingApi } from '@/hooks/usePollingApi';
import { qryCopilotValueApi, getCopilotValueByTaskIdApi } from '@/api/copilot';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { EXTERNAL_USER_ID } from '@/config/env';
import useUserStore from '@/stores/userStore';
import useCopilotValueStore from '@/stores/copilotValueStore';
import { CopilotValue17, CopilotValue4 } from '@/stores/types/copilotValue';

export const useAllocationData = (initialHasData = false) => {
  const { externalUserId, followUserId } = useAuth();
  const { taskId: initialTaskId, updateTaskId } = useUserStore();
  const [loading, setLoading] = useState<boolean>(true);
  const [generatePlanLoading, setGeneratePlanLoading] = useState<boolean>(false);
  const [hasData, setHasData] = useState<boolean>(initialHasData);
  const { updateCopilotValue4, updateCopilotValue17 } = useCopilotValueStore();
  const hasFetched = useRef(false);

  const { startPolling } = usePollingApi(
    taskId =>
      getCopilotValueByTaskIdApi({
        externalUserId,
        reqType: 4,
        taskId: taskId || '',
        followUserId
      }),
    response => response?.status === 'false',
    {
      interval: 1000,
      onSuccess: data => {
        setGeneratePlanLoading(false);
        setHasData(true);
        updateCopilotValue4(data);
      },
      onError: error => {
        console.error('轮询失败:', error);
        toast.error(error || '数据获取失败');
        setGeneratePlanLoading(false);
        setHasData(false);
        updateCopilotValue4({});
      }
    }
  );

  const initCopilotValue17 = async (taskId?: string) => {
    try {
      const response = await getCopilotValueByTaskIdApi({
        externalUserId: externalUserId || EXTERNAL_USER_ID,
        reqType: 17,
        taskId: taskId || initialTaskId,
        followUserId
      });
      updateCopilotValue17(response as CopilotValue17);
    } catch (error) {
      console.error('获取宏观市场环境分析失败:', error);
    }
  };

  const initData = async (taskId?: string) => {
    initCopilotValue17(taskId);

    try {
      setLoading(true);
      const res = await getCopilotValueByTaskIdApi({
        externalUserId,
        reqType: 4,
        taskId: taskId || initialTaskId,
        followUserId
      });
      updateCopilotValue4(res as CopilotValue4);
    } catch (error) {
      console.error('获取数据失败:', error);
      toast.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchInitialData = async () => {
    if (!initialTaskId) {
      setLoading(false);
      qryCopilotValueApi({
        externalUserId,
        reqType: 0,
        followUserId
      }).then(response => {
        updateTaskId(response as string);
        initData(response);
      });
      return;
    }
    initData();
  };

  const generatePlan = async (text: string) => {
    try {
      setGeneratePlanLoading(true);
      const response = await qryCopilotValueApi({
        externalUserId,
        reqType: 1,
        text,
        followUserId
      });
      startPolling(response);
    } catch (error) {
      console.error('生成方案失败:', error);
      toast.error('生成方案失败');
      setGeneratePlanLoading(false);
    }
  };

  return {
    loading,
    generatePlanLoading,
    hasData,
    hasFetched,
    fetchInitialData,
    generatePlan,
    initData
  };
};
